/* eslint-disable max-len */
import AppBar from '@components/common/navigation/appBar';
import {
  ActionIcon,
  Anchor,
  AspectRatio,
  Box,
  Button,
  Container,
  Group,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { WeekDay } from '@models/ropolo';
import { useVendorAvailability } from '@services/availabilitytime/hooks/useVendorAvailability';
import { IconBrandGoogleMaps, IconBrandWaze } from '@tabler/icons-react';
import { groupBy } from 'lodash';
import 'mapbox-gl/dist/mapbox-gl.css';
import Image from 'next/image';
import { Map, Marker } from 'react-map-gl';
import storageUtil from '../../../../../utils/storageUtil';
import { HomePageInfoProps } from '../../types';

const translateWeekday = (weekday: WeekDay): string => {
  const daysOfWeek = {
    SAT: 'شنبه',
    SUN: 'یک‌شنبه',
    MON: 'دوشنبه',
    TUE: 'سه‌شنبه',
    WED: 'چهارشنبه',
    THU: 'پنج‌شنبه',
    FRI: 'جمعه',
  };

  return daysOfWeek[weekday] || weekday;
};

function generateWazeLink(lat?: number, lon?: number): string {
  return `https://www.waze.com/ul?ll=${lat},${lon}&navigate=yes`;
}

function generateGoogleMapsLink(lat?: number, lon?: number): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`;
}

const VendorInfoModal = (props: HomePageInfoProps) => {
  const theme = useMantineTheme();

  const { data } = useVendorAvailability();

  const { opened, onClose, vendor } = props;

  const logoUrl =
    vendor?.subDomain && vendor?.logo
      ? storageUtil.getItemImage(vendor.subDomain, vendor.logo)
      : storageUtil.getCoverImage('ss-icon.svg');

  const header = (
    <Modal.Header p={0} display="block">
      <AppBar
        text="اطلاعات"
        rightActions={<Modal.CloseButton size="md" />}
        leftActions={
          <ActionIcon variant="subtle" size="md">
            {null}
          </ActionIcon>
        }
      />
    </Modal.Header>
  );

  const times = groupBy(data?.data?.availabilityTimes, 'dayOfWeek');

  const translatedData = Object.fromEntries(
    Object.entries(times).map(([dayOfWeek, items]) => [
      translateWeekday(dayOfWeek as WeekDay),
      items.map((item: any) => ({ ...item, dayOfWeek: translateWeekday(dayOfWeek as WeekDay) })),
    ])
  );

  return (
    <Modal.Root
      opened={opened}
      onClose={onClose}
      transitionProps={{ transition: 'slide-right', duration: 200 }}
      zIndex={202}
      fullScreen
    >
      <Modal.Overlay />
      <Modal.Content>
        {header}
        <Modal.Body>
          <Container size="lg">
            <Box>
              <Stack>
                <Group align="flex-start" wrap="nowrap">
                  <AspectRatio w={84} ratio={1 / 1}>
                    <Image
                      alt={vendor?.name || ''}
                      width={84}
                      height={84}
                      src={logoUrl}
                      style={{ borderRadius: theme.radius.lg }}
                    />
                  </AspectRatio>
                  <Box>
                    <Stack gap="xs">
                      <Title order={2} size="h5">
                        {vendor?.name}
                      </Title>

                      <Text size="sm">{vendor?.address}</Text>
                    </Stack>
                  </Box>
                </Group>

                <Group gap={5}>
                  تماس:
                  <Anchor size="sm" color="blue.9" href={`tel:${vendor?.phoneNumber}`}>
                    {vendor?.phoneNumber}
                  </Anchor>
                </Group>

                <Paper>
                  <Paper shadow="xs" withBorder>
                    <Map
                      latitude={vendor?.latitude}
                      longitude={vendor?.longitude}
                      zoom={14}
                      style={{ height: 250, width: '100%' }}
                      mapStyle="https://tile.snappmaps.ir/styles/snapp-style/style.json"
                      mapboxAccessToken="pk.eyJ1IjoibW9udGFqZWJpIiwiYSI6ImNsdjdzb2o3ZzBkZGYyamxicTc1bWtzN2UifQ.0TLTkncOLKeihvGQnhf0rw"
                    >
                      <Marker
                        latitude={vendor?.latitude || 0}
                        longitude={vendor?.longitude || 0}
                        color="black"
                        style={{ zIndex: 1 }}
                      />
                    </Map>
                  </Paper>
                </Paper>

                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={{ xs: 'sm' }}>
                  <Button
                    leftSection={<IconBrandWaze />}
                    component="a"
                    target="_blank"
                    href={generateWazeLink(vendor?.latitude, vendor?.longitude)}
                    variant="outline"
                  >
                    مسیریابی با waze
                  </Button>

                  <Button
                    leftSection={<IconBrandGoogleMaps />}
                    component="a"
                    target="_blank"
                    href={generateGoogleMapsLink(vendor?.latitude, vendor?.longitude)}
                    variant="outline"
                  >
                    مسیریابی با گوگل
                  </Button>
                </SimpleGrid>

                <Table withRowBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>
                        <Text ta="center">روز کاری</Text>
                      </Table.Th>
                      <Table.Th>
                        <Text ta="center">ساعت کاری</Text>
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {Object.entries(translatedData).map(([dayOfWeek, items]) => (
                      <Table.Tr key={dayOfWeek}>
                        <Table.Td>
                          <Text ta="center">{dayOfWeek}</Text>
                        </Table.Td>
                        <Table.Td colSpan={2}>
                          <ul>
                            {items.map((item) => (
                              <li key={item.id}>
                                <Text size="sm" ta="center">
                                  {item.startTime} - {item.endTime}
                                </Text>
                              </li>
                            ))}
                          </ul>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Stack>
            </Box>

            <Box mb={30} />
          </Container>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};

export default VendorInfoModal;
